import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Download, FileText, Image, File, Presentation } from 'lucide-react';
import { exportToCSV, exportToPNG, exportToPDF, exportToPPT } from '../../utils/exportUtils';
import { toast } from 'sonner';

/**
 * ExportMenu Component
 * Provides export functionality in CSV, PNG, PDF, and PPT formats
 * 
 * @param {Object} props
 * @param {Object} props.data - Data to export (for CSV)
 * @param {string|HTMLElement} props.element - Element or selector to capture (for PNG/PDF/PPT)
 * @param {string} props.filename - Base filename for exports
 * @param {string} props.title - Title for PDF/PPT exports
 * @param {boolean} props.disabled - Whether the button is disabled
 */
const ExportMenu = ({ 
  data = null, 
  element = null, 
  filename = 'export', 
  title = 'Report',
  disabled = false 
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format) => {
    setIsExporting(true);
    toast.info(`Exporting as ${format.toUpperCase()}...`);

    try {
      let result;

      switch (format) {
        case 'csv':
          if (!data || !Array.isArray(data)) {
            toast.error('No data available for CSV export');
            setIsExporting(false);
            return;
          }
          result = exportToCSV(data, filename);
          break;

        case 'png':
          if (!element) {
            toast.error('No element specified for PNG export');
            setIsExporting(false);
            return;
          }
          result = await exportToPNG(element, filename);
          break;

        case 'pdf':
          if (!element) {
            toast.error('No element specified for PDF export');
            setIsExporting(false);
            return;
          }
          result = await exportToPDF(element, filename, title);
          break;

        case 'ppt':
          if (!element) {
            toast.error('No element specified for PPT export');
            setIsExporting(false);
            return;
          }
          result = await exportToPPT(element, filename, title);
          break;

        default:
          toast.error('Unknown export format');
          setIsExporting(false);
          return;
      }

      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          disabled={disabled || isExporting}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Export Format</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data && (
          <DropdownMenuItem onClick={() => handleExport('csv')} disabled={isExporting}>
            <FileText className="h-4 w-4 mr-2" />
            Export as CSV
          </DropdownMenuItem>
        )}
        {element && (
          <>
            <DropdownMenuItem onClick={() => handleExport('png')} disabled={isExporting}>
              <Image className="h-4 w-4 mr-2" />
              Export as PNG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('pdf')} disabled={isExporting}>
              <File className="h-4 w-4 mr-2" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('ppt')} disabled={isExporting}>
              <Presentation className="h-4 w-4 mr-2" />
              Export as PPT
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportMenu;
